import { getUserOptions, searchOrgsOptions, searchUsersOptions } from "@payezy/api-client/admin/query";
import { Badge, Card, Dialog, EmptyState, formatDate, Input, PageHeader, Select, Table, Tabs, TD, TH, THead, TR, humanize } from "@payezy/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useDeferredValue, useState } from "react";
import { orgStatusTone } from "../lib/staff";

export function CustomersPage() {
  const [tab, setTab] = useState("orgs");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const dq = useDeferredValue(q.trim());
  return (
    <>
      <PageHeader title="Customers" description="Find an organization or person. Opening a customer is recorded in the audit log." />
      <Tabs value={tab} onValueChange={setTab} tabs={[{ value: "orgs", label: "Organizations" }, { value: "users", label: "People" }]} />
      <div className="mb-4 flex gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-3 size-4 text-ink-400" />
          <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder={tab === "orgs" ? "Organization name" : "Email or name"} autoFocus />
        </div>
        {tab === "orgs" && (
          <Select className="w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.keys(orgStatusTone).map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
          </Select>
        )}
      </div>
      {tab === "orgs" ? <OrgResults q={dq} status={status} /> : <UserResults q={dq} />}
    </>
  );
}

function OrgResults({ q, status }: { q: string; status: string }) {
  const orgs = useQuery({ ...searchOrgsOptions({ query: { q: q || undefined, status: status || undefined, limit: 50 } }), placeholderData: keepPreviousData });
  const items = orgs.data?.items ?? [];
  return (
    <Card>
      <Table>
        <THead><TR><TH>Organization</TH><TH>Type</TH><TH>Status</TH><TH className="text-right">Single / daily limit</TH><TH>Opened</TH></TR></THead>
        <tbody>
          {items.map((o) => (
            <TR key={o.id} className="hover:bg-ink-50">
              <TD><Link to="/customers/$orgId" params={{ orgId: o.id }} className="font-medium text-ink-950 hover:underline">{o.name}</Link></TD>
              <TD>{humanize(o.type)}</TD>
              <TD><Badge tone={orgStatusTone[o.status]}>{humanize(o.status)}</Badge></TD>
              <TD className="text-right tabular-nums">${o.single_payout_limit} / ${o.daily_payout_limit}</TD>
              <TD>{formatDate(o.created_at)}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
      {!orgs.isLoading && items.length === 0 && <EmptyState title="No organizations match" />}
    </Card>
  );
}

function UserResults({ q }: { q: string }) {
  const users = useQuery({ ...searchUsersOptions({ query: { q, limit: 50 } }), enabled: q.length >= 2, placeholderData: keepPreviousData });
  const [selected, setSelected] = useState<string | null>(null);
  const items = q.length >= 2 ? (users.data?.items ?? []) : [];
  return (
    <Card>
      <Table>
        <THead><TR><TH>Name</TH><TH>Email</TH><TH>Status</TH><TH>Last seen</TH></TR></THead>
        <tbody>
          {items.map((u) => (
            <TR key={u.id} className="cursor-pointer hover:bg-ink-50" onClick={() => setSelected(u.id)}>
              <TD className="font-medium">{`${u.first_name} ${u.last_name}`.trim() || "—"}</TD>
              <TD>{u.email}</TD>
              <TD><Badge tone={u.status === "active" ? "success" : "danger"}>{u.status}</Badge></TD>
              <TD>{formatDate(u.last_seen_at, true)}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
      {items.length === 0 && <EmptyState title={q.length < 2 ? "Type at least two characters" : "No people match"} />}
      {selected && <UserDialog id={selected} onClose={() => setSelected(null)} />}
    </Card>
  );
}

function UserDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const u = useQuery(getUserOptions({ path: { userId: id } }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={u.data ? u.data.user.email : "Loading…"} description="Organizations this person belongs to.">
      <div className="flex flex-col divide-y divide-ink-100">
        {u.data?.memberships.map((m) => (
          <Link key={m.org_id} to="/customers/$orgId" params={{ orgId: m.org_id }} className="flex items-center justify-between py-2.5 text-sm hover:bg-ink-50">
            <span className="font-medium">{m.org_name}</span>
            <span className="flex items-center gap-2 text-ink-500">{m.role}<Badge tone={orgStatusTone[m.org_status]}>{humanize(m.org_status)}</Badge></span>
          </Link>
        ))}
        {u.data && u.data.memberships.length === 0 && <p className="py-2 text-sm text-ink-500">No memberships.</p>}
      </div>
    </Dialog>
  );
}
